import { useCallback, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { TAB_SLUGS } from '@scoutaround/script-breakdown-shared';

import { api } from '../../lib/api.js';
import { useCan, useRefusal } from '../../lib/permissions.js';
import { Icon } from '../../components/Icon.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { FileDetailPanel } from '../../components/files/FileDetailPanel.js';
import { EmptyFiles } from '../../components/files/EmptyFiles.js';
import { FilesLibrary } from '../../components/files/FilesLibrary.js';
import { UploadSheet } from '../../components/files/UploadSheet.js';
import { useFilesAddress } from '../../components/files/address.js';
import { useFilesMeta, useInvalidateFiles, useProjectFiles } from '../../components/files/model.js';
import { acceptAttribute, uploadLimitText } from '../../components/references/format.js';
import { useUploadQueue, uploadSummaryText, type QueuedUpload, type UploadControl } from '../../components/upload/useUploadQueue.js';
import '../../styles/team.css';
import '../../styles/references.css';
import '../../styles/files.css';

/**
 * FILES — the production's agreements, permits and location documents, in the
 * layout of the References library (2026-11-02): the page's header, a drop bar,
 * the list beside the open file, and an upload sheet that follows each file.
 *
 * ONE RED BUTTON. With files here it is the header's Upload files; with none it is
 * the empty state's Upload your first file, and the header's turns neutral. A person
 * who may not upload — their role, a plan without media storage, a read-only
 * production — sees why, and no red button at all.
 *
 * NOTHING IS DOWNLOADABLE YET, AND THE PAGE SAYS SO. Every file is held until it is
 * checked and no check exists, so no file is offered for download; the open file
 * says it is kept privately and will be downloadable once checking is available.
 */
/**
 * The role's refusal ("Not something an Observer does on this production. …") with the
 * act it refuses named first, so the sentence says what cannot be done.
 */
function naming(act: string, refusal: string | null): string | null {
  if (refusal === null) return null;
  return refusal.startsWith('Not something ') ? `${act} is not${refusal.slice(3)}` : `${act}: ${refusal}`;
}

export function FilesTab({ projectId }: { projectId: string }) {
  const { address, update } = useFilesAddress();
  const files = useProjectFiles(projectId);
  const meta = useFilesMeta(projectId);
  const invalidate = useInvalidateFiles(projectId);
  const canUpload = useCan('FILE_UPLOAD');
  const uploadRefusal = naming('Uploading files', useRefusal('FILE_UPLOAD'));
  const canManage = useCan('FILE_MANAGE');
  const manageRefusal = naming('Deleting files', useRefusal('FILE_MANAGE'));

  const [sheetOpen, setSheetOpen] = useState(false);
  const [over, setOver] = useState(false);
  const [overLibrary, setOverLibrary] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [detailTakesFocus, setDetailTakesFocus] = useState(true);
  const picker = useRef<HTMLInputElement>(null);

  const send = useCallback(
    (item: QueuedUpload<true>, control: UploadControl) =>
      api.uploadFile(projectId, item.file, { clientRequestId: item.clientRequestId }, control),
    [projectId],
  );
  const uploads = useUploadQueue<true>(meta.data?.upload, send, () => {
    void invalidate();
  });

  const readOnly = meta.data?.readOnly === true;
  const planBlocked = meta.data?.upload.allowed === false;
  const uploadOffered = canUpload && !readOnly && !planBlocked;
  const isEmpty = files.data !== undefined && files.data.length === 0;
  const busy = uploads.summary.active + uploads.summary.queued;

  const addFiles = (chosen: File[]) => {
    if (chosen.length === 0) return;
    setNotice(null);
    uploads.add(chosen, true);
    setSheetOpen(true);
  };

  const choose = () => {
    picker.current?.click();
  };

  const closeSheet = () => {
    // A finished batch is cleared; one with failures stays so they can be retried.
    if (uploads.summary.active === 0 && uploads.summary.queued === 0 && uploads.summary.failed === 0) uploads.reset();
    setSheetOpen(false);
  };

  // Why nothing can be uploaded here, said once where the drop bar would be.
  const gate: ReactNode = readOnly ? (
    <div className="files-gate" data-testid="files-readonly">
      <Icon name="lock" size={16} />
      <p>
        This production is read-only: its files stay here to read, and nothing can be uploaded or deleted until it is
        renewed.{' '}
        <Link to={`/projects/${projectId}/${TAB_SLUGS.BILLING}`} data-testid="files-readonly-billing">
          Plan &amp; billing
        </Link>
      </p>
    </div>
  ) : planBlocked ? (
    <div className="files-gate" data-testid="files-plan">
      <Icon name="lock" size={16} />
      <p>
        Uploading files is not part of this production’s plan. Files already here stay readable; a plan with media
        storage adds uploads.{' '}
        <Link to={`/projects/${projectId}/${TAB_SLUGS.BILLING}`} data-testid="files-plan-billing">
          See Plan &amp; billing
        </Link>
      </p>
    </div>
  ) : !canUpload && uploadRefusal !== null ? (
    <div className="files-gate" data-testid="files-upload-refusal">
      <Icon name="lock" size={16} />
      <p>{uploadRefusal}</p>
    </div>
  ) : null;

  const header = uploadOffered ? (
    <button
      type="button"
      className={isEmpty ? 'btn' : 'btn btn-primary'}
      data-testid="files-header-upload"
      onClick={choose}
    >
      <Icon name="upload" size={16} strokeWidth={2} />
      Upload files
    </button>
  ) : undefined;

  const emptyState = (
    <EmptyFiles
      action={
        uploadOffered ? (
          <button type="button" className="btn btn-primary" data-testid="files-empty-upload" onClick={choose}>
            <Icon name="upload" size={16} strokeWidth={2} />
            Upload your first file
          </button>
        ) : null
      }
      note={
        uploadOffered ? (
          <p className="ref-quiet" data-testid="files-empty-drop">
            Or drop files anywhere on this panel.
          </p>
        ) : !canUpload ? (
          <p className="ref-quiet" data-testid="files-empty-refusal">
            {uploadRefusal ?? 'You can see this production’s files but not add to them.'}
          </p>
        ) : null
      }
    />
  );

  const selected = address.file === null ? null : (files.data?.find((f) => f.id === address.file) ?? null);
  const deleteRefusal = readOnly
    ? 'This production is read-only, so files cannot be deleted.'
    : canManage
      ? null
      : manageRefusal;

  return (
    <>
      <section className="files-page" data-testid="files-tab">
        <PageHeader
          title="Files"
          lede="Agreements, permits and location documents for this production, in one place."
          actions={header}
          testId="files-header"
        />

        {uploadOffered ? (
          <div
            className="ref-drop files-drop"
            data-over={over ? '1' : undefined}
            data-testid="files-drop"
            onDragOver={(event) => {
              event.preventDefault();
              setOver(true);
            }}
            onDragLeave={() => {
              setOver(false);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setOver(false);
              addFiles(Array.from(event.dataTransfer.files));
            }}
          >
            <Icon name="cloudUpload" size={18} />
            <span className="ref-drop-text">
              <span>
                {'Drag & drop files here or '}
                <button type="button" className="ref-drop-browse" data-testid="files-drop-browse" onClick={choose}>
                  browse
                </button>
              </span>
              {meta.data ? (
                <span className="ref-drop-limit" data-testid="files-drop-limit">
                  {uploadLimitText(meta.data.upload)}
                </span>
              ) : null}
            </span>
          </div>
        ) : (
          gate
        )}

        {uploadOffered ? (
          <input
            ref={picker}
            type="file"
            multiple
            hidden
            accept={meta.data ? acceptAttribute(meta.data.upload) : undefined}
            data-testid="files-file-input"
            onChange={(event) => {
              const chosen = Array.from(event.target.files ?? []);
              event.target.value = '';
              addFiles(chosen);
            }}
          />
        ) : null}

        {!sheetOpen && (busy > 0 || uploads.summary.failed > 0) ? (
          <p className="ref-upload-banner" role="status" data-testid="files-upload-banner">
            <Icon name="upload" size={15} />
            <span>{uploadSummaryText(uploads.summary, 'uploaded')}</span>
            <button
              type="button"
              className="ref-text-btn"
              data-testid="files-upload-banner-open"
              onClick={() => {
                setSheetOpen(true);
              }}
            >
              Show uploads
            </button>
          </p>
        ) : null}

        {notice !== null ? (
          <p className="ref-notice files-notice" role="status" data-testid="files-notice">
            {notice}
          </p>
        ) : null}

        <div className="files-layout" data-detail={address.file === null ? 'closed' : 'open'} data-testid="files-layout">
          <section
            className="files-library"
            aria-label="Files"
            data-over={overLibrary ? '1' : undefined}
            onDragOver={
              uploadOffered
                ? (event) => {
                    event.preventDefault();
                    setOverLibrary(true);
                  }
                : undefined
            }
            onDragLeave={
              uploadOffered
                ? (event) => {
                    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOverLibrary(false);
                  }
                : undefined
            }
            onDrop={
              uploadOffered
                ? (event) => {
                    event.preventDefault();
                    setOverLibrary(false);
                    addFiles(Array.from(event.dataTransfer.files));
                  }
                : undefined
            }
          >
            <FilesLibrary
              projectId={projectId}
              files={files.data}
              loading={files.isPending}
              error={files.error}
              onRetry={() => {
                void files.refetch();
              }}
              address={address}
              update={update}
              empty={emptyState}
              onNavigate={(via) => {
                setDetailTakesFocus(via === 'open');
              }}
            />
          </section>

          <aside
            className="files-detail"
            aria-label="File"
            data-open={address.file === null ? undefined : '1'}
            data-testid="files-detail"
          >
            {address.file === null ? (
              <div className="ref-detail-placeholder" data-testid="files-detail-placeholder">
                <Icon name="file" size={34} strokeWidth={1.4} />
                <p className="ref-detail-placeholder-title">Select a file</p>
                <p className="ref-quiet">Open a file from the list to see who uploaded it, when, and where it stands.</p>
              </div>
            ) : (
              <FileDetailPanel
                key={address.file}
                projectId={projectId}
                fileId={address.file}
                file={selected}
                loading={files.isPending}
                takeFocus={detailTakesFocus}
                canDelete={canManage && !readOnly}
                deleteRefusal={deleteRefusal}
                onClose={() => {
                  update({ file: null });
                }}
                onDeleted={(name) => {
                  setNotice(`“${name}” was deleted. ScoutAround keeps the file, so it can be recovered.`);
                  update({ file: null });
                }}
              />
            )}
          </aside>
        </div>
      </section>

      {sheetOpen ? (
        <UploadSheet uploads={uploads} meta={meta.data} onChoose={choose} onDrop={addFiles} onClose={closeSheet} />
      ) : null}
    </>
  );
}
